import { createContext, useEffect, useMemo, useState } from 'react';
import {
    EUserRole,
    EUserStatus,
    EUserType,
    TBusinessProfile,
    TIndividualProfile
} from '@xeppt/xeppt-sdk/types/user';
import { apiAccountService, apiAuthService, apiUserService } from '@api';
import { routes } from '@const/routes';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { isBusinessUserTypeGuard, TBusinessDetails } from '@types';
import { useApiQuery } from '@hooks/api/useApiQuery';
import {
    TAccount,
    TAccountLimits,
    TCashbackStat,
    TTransactionMethodTypeAction
} from '@xeppt/xeppt-sdk/types';
import { AnimatePresence } from 'framer-motion';
import moment from 'moment-timezone';
import { NavigationProvider } from '@context/navigation_context';
import { ELoyaltyLevel, TMyLoyalty } from '@xeppt/xeppt-sdk/types/loyalty';
import { useTranslation } from 'react-i18next';
import { TProduct } from '@xeppt/xeppt-sdk/types/products';

type TUserContext = {
    user?: TIndividualProfile | TBusinessProfile;
    account?: TAccount;
    userStatus?: EUserStatus;
    loyalty?: TMyLoyalty;
    limits?: Record<TTransactionMethodTypeAction, TAccountLimits>;
    limitsSettings?: Record<ELoyaltyLevel, Record<TTransactionMethodTypeAction, TAccountLimits>>;
    cashback?: TCashbackStat;
    products: Omit<TProduct, 'userType'>[];
    businessData?: TBusinessDetails;
    handleLogout: (
        isRedirect?: boolean,
        state?: Record<string, any>,
        redirectRoute?: string
    ) => void;
    refetchAccount: () => void;
    refetchCashback: () => void;
    handleChangeUserStatus: (status: EUserStatus) => void;
    refetchLoyalty: () => void;
    refetchUser: () => void;
    isDataLoading: boolean;
};

const initialState: TUserContext = {
    user: undefined,
    account: undefined,
    userStatus: undefined,
    cashback: undefined,
    products: [],
    limits: undefined,
    loyalty: undefined,
    businessData: undefined,
    handleLogout: () => Promise.resolve(),
    refetchAccount: () => Promise.resolve(),
    refetchLoyalty: () => Promise.resolve(),
    refetchCashback: () => Promise.resolve(),
    refetchUser: () => Promise.resolve(),
    handleChangeUserStatus: () => {},
    isDataLoading: true
};

export const authRoutes = [
    routes.hidden_signin,
    routes.hidden_forgot_password,
    routes.hidden_signup
];

export const publicPages = [
    routes.terms_of_service,
    routes.privacy_policy,
    routes.extension_install,
    routes.extension_uninstall,
    //remove after launch
    routes.signin,
    routes.forgot_password,
    routes.signup
];

export const businessRoutes = [routes.gift_cards, routes.gift_cards_create];

export const UserContext = createContext<TUserContext>(initialState);

export const UserContextProvider = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isFirstRender, setIsFirstRender] = useState(true);
    const [businessData, setBusinessData] = useState<TBusinessDetails>();
    const [user, setUser] = useState<TBusinessProfile | TIndividualProfile>();
    const [userStatus, setUserStatus] = useState<EUserStatus>();
    const [cashback, setCashback] = useState<TCashbackStat>();
    const [account, setAccount] = useState<TAccount>();
    const [limits, setLimits] = useState<Record<TTransactionMethodTypeAction, TAccountLimits>>();
    const [limitsSettings, setLimitsSettings] =
        useState<Record<ELoyaltyLevel, Record<TTransactionMethodTypeAction, TAccountLimits>>>();
    const [loyalty, setLoyalty] = useState<TMyLoyalty>();
    const [products, setProducts] = useState<Omit<TProduct, 'userType'>[]>([]);
    const { i18n } = useTranslation();
    useEffect(() => {
        if (!!user) {
            i18n.changeLanguage(user.profile.lang || (localStorage.getItem('language') as string));
        }
    }, [user]);

    const isAuthRoute = useMemo(() => authRoutes.includes(location.pathname), [location]);
    const isPublicPage = useMemo(() => publicPages.includes(location.pathname), [location]);

    const handleLogout = (
        isRedirect?: boolean,
        state?: Record<string, any>,
        redirectRoute?: string
    ) => {
        apiAuthService
            .logout()
            .then(() => {
                navigate(redirectRoute || routes.signin, {
                    state: {
                        redirect: isRedirect ? location.pathname + location.search : undefined,
                        ...state
                    }
                });
            })
            .catch(() => {});
        setUser(undefined);
        setCashback(undefined);
        setBusinessData(undefined);
        setLoyalty(undefined);
        setLimits(undefined);
        setLimitsSettings(undefined);
        setAccount(undefined);
        localStorage.removeItem('client-metadata');
    };

    const { isLoading: isCashbackLoading, handleRequest: refetchCashback } = useApiQuery({
        method: () => apiAccountService.getCashbackStat(),
        condition: !!user,
        deps: [user],
        onSuccess: (data) => setCashback(data)
    });

    const { isLoading: isUserLoading, handleRequest: refetchUser } = useApiQuery({
        method: () => apiUserService.profile(),
        deps: [isAuthRoute],
        condition: !isAuthRoute,
        onSuccess: (data) => {
            moment.tz.setDefault(data.profile.timezone);
            if (data.role !== EUserRole.REGULAR_USER) {
                handleLogout();
            }
            setUser(data);
            setUserStatus(data.status);
            if (isBusinessUserTypeGuard(data)) {
                if (data.business) {
                    const { business } = data;
                    setBusinessData(business);
                }
            }
        },
        onError: () => {
            if (!isPublicPage) {
                handleLogout(true);
            }
        }
    });

    const { handleRequest: refetchAccount, isLoading: isAccountLoading } = useApiQuery({
        method: () => apiAccountService.getAccount(),
        onSuccess: (data) => {
            setAccount(data);
        },
        condition: !!user,
        deps: [user]
    });

    const { handleRequest: refetchLoyalty, isLoading: isLoyaltyLoading } = useApiQuery({
        method: () => apiAccountService.getMyLoyalty(),
        onSuccess: (data) => {
            setLoyalty(data);
        },
        condition: !!user,
        deps: [user]
    });

    const { handleRequest: refetchProducts, isLoading: isProductsLoading } = useApiQuery({
        method: () => apiAccountService.getServiceProducts(),
        onSuccess: (data) => {
            setProducts(data);
        },
        condition: !!user,
        deps: [user]
    });

    const { handleRequest: refetchLimits, isLoading: isLimitsLoading } = useApiQuery({
        method: () => apiAccountService.getLimits(),
        onSuccess: (data) => {
            setLimitsSettings(data.settings);
            setLimits(data.limits);
        },
        condition: !!user,
        deps: [user]
    });

    const isDataLoading = useMemo(() => {
        return (
            isUserLoading ||
            isAccountLoading ||
            isCashbackLoading ||
            isLoyaltyLoading ||
            isLimitsLoading ||
            isProductsLoading
        );
    }, [
        isUserLoading,
        isAccountLoading,
        isCashbackLoading,
        isLoyaltyLoading,
        isLimitsLoading,
        isProductsLoading
    ]);

    useEffect(() => {
        if (!isFirstRender && !isDataLoading) {
            if (!user && !isAuthRoute && !isPublicPage) {
                navigate(routes.signin, {
                    state: { redirect: location.pathname + location.search }
                });
            }
        } else if (isFirstRender && !isDataLoading) {
            setIsFirstRender(false);
        }
    }, [isDataLoading, user]);

    useEffect(() => {
        if (user && location) {
            if (user.type !== EUserType.BUSINESS && businessRoutes.includes(location.pathname)) {
                navigate(routes.not_found);
            }
        }
    }, [location, user]);

    return (
        <NavigationProvider>
            <UserContext.Provider
                value={{
                    user,
                    account,
                    businessData,
                    refetchCashback,
                    userStatus,
                    refetchLoyalty,
                    loyalty,
                    limits,
                    products,
                    limitsSettings,
                    cashback,
                    handleLogout,
                    handleChangeUserStatus: (status: EUserStatus) => setUserStatus(status),
                    refetchAccount: () => {
                        refetchAccount();
                        refetchLoyalty();
                        refetchLimits();
                        refetchProducts();
                    },
                    refetchUser,
                    isDataLoading
                }}>
                {/*//@ts-ignore*/}
                <AnimatePresence>
                    <Outlet />
                </AnimatePresence>
            </UserContext.Provider>
        </NavigationProvider>
    );
};
